import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Finding, FindingStatus, ReviewDepth, ReviewListParams } from '../types/api';

export const keys = {
  config: ['config'] as const,
  stats: ['stats'] as const,
  pullRequests: ['pull-requests'] as const,
  reviews: (p: ReviewListParams) => ['reviews', p] as const,
  review: (id: string) => ['review', id] as const,
  findings: (id: string) => ['findings', id] as const,
};

const isActive = (s?: string) => s === 'queued' || s === 'processing';

export const useConfig = () => useQuery({ queryKey: keys.config, queryFn: api.config, staleTime: 60_000 });

export const useStats = () =>
  useQuery({
    queryKey: keys.stats,
    queryFn: api.stats,
    // Keep the dashboard current while reviews are running (covers webhook-triggered reviews too).
    refetchInterval: (q) => (q.state.data && (q.state.data.byStatus.queued || q.state.data.byStatus.processing) ? 3000 : 30_000),
  });

export const usePullRequests = () => useQuery({ queryKey: keys.pullRequests, queryFn: api.pullRequests });

export const useReviews = (params: ReviewListParams) =>
  useQuery({ queryKey: keys.reviews(params), queryFn: () => api.listReviews(params), placeholderData: keepPreviousData });

export function useReview(id: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: keys.review(id),
    queryFn: async () => {
      const review = (await api.getReview(id)).review;
      // A failed run refunds its credit; keep the balance in the header current.
      if (review.status === 'failed' || review.status === 'completed') void qc.invalidateQueries({ queryKey: ['me'] });
      return review;
    },
    refetchInterval: (q) => (isActive(q.state.data?.status) ? 1500 : false),
  });
}

export function useFindings(id: string, enabled: boolean) {
  return useQuery({ queryKey: keys.findings(id), queryFn: () => api.findings(id).then((r) => r.items), enabled });
}

export function useCreateReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { prUrl: string; depth: ReviewDepth; focus?: string }) => api.createReview(body),
    // The credit balance changes on success, and a 402 means our cached balance was stale.
    onSettled: () => void qc.invalidateQueries({ queryKey: ['me'] }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reviews'] });
      void qc.invalidateQueries({ queryKey: keys.stats });
      void qc.invalidateQueries({ queryKey: keys.pullRequests });
    },
  });
}

export function useRetryReview(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.retryReview(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['me'] }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.review(id) });
      void qc.invalidateQueries({ queryKey: keys.stats });
    },
  });
}

export function useUpdateFinding(reviewId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: FindingStatus; dismissReason?: string }) =>
      api.updateFinding(v.id, { status: v.status, dismissReason: v.dismissReason }).then((r) => r.finding),
    onSuccess: (finding) => {
      qc.setQueryData<Finding[]>(keys.findings(reviewId), (old) => old?.map((f) => (f.id === finding.id ? finding : f)));
    },
  });
}

export function usePublishComments(reviewId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (findingIds: string[]) => api.publishComments(reviewId, findingIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.findings(reviewId) });
      void qc.invalidateQueries({ queryKey: keys.review(reviewId) });
    },
  });
}
